import { Search } from "@mui/icons-material";
import {
  Box,
  TextField,
  Typography,
  useMediaQuery,
  Button,
  Grid
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tags } from "../../assets/dummyData";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

const HomePageSearchBar = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("Women");

  const [index, setIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [showText, setShowText] = useState(true);

  const { homepageConfig } = useSelector((state) => state.uiContent);
  const queries = homepageConfig?.searchBarQueries;

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const currentTag = tags[index]?.name || "a matching co-ord set";
    setTypedText("");
    setShowText(true);

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      setTypedText(currentTag.slice(0, charIndex + 1));
      charIndex++;
      if (charIndex === currentTag.length) {
        clearInterval(typingInterval);
        setTimeout(() => setShowText(false), 1500);
      }
    }, 80);

    return () => clearInterval(typingInterval);
  }, [index]);

  useEffect(() => {
    if (!showText) {
      const timeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % tags.length);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [showText]);

  const handleSearch = () => {
    if (search.trim().length > 0) {
      navigate('/search', { state: { search, gender } });
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FFFFFF",
        backgroundImage: "radial-gradient(circle at 50% -20%, rgba(255,107,0,0.08) 0%, rgba(255,255,255,0) 70%)",
        pt: isMobile ? 6 : isTablet ? 8 : 10,
        pb: 2,
        px: 2,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderBottom: "1px solid #EAEAEA"
      }}
    >


      <Box
        sx={{
          maxWidth: "800px",
          width: "100%",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <Typography
            sx={{
              fontFamily: "'Lora', serif",
              fontSize: { xs: "32px", sm: "44px", md: "54px", lg: "64px" },
              fontWeight: 500,
              color: "#111111",
              lineHeight: 1.1,
              mb: { xs: 1, md: 1.5 },
              letterSpacing: "-0.03em",
            }}
          >
            Find the exact outfit you <br />
            <Box
              component="span"
              sx={{
                fontStyle: "italic",
                fontWeight: 600,
                background: "linear-gradient(110deg, #FF6B00 0%, #FF6B00 40%, #FFD166 50%, #FF6B00 60%, #FF6B00 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "neon-shimmer 5s ease-in-out infinite",
                "@keyframes neon-shimmer": {
                  "0%": {
                    backgroundPosition: "150% center",
                  },
                  "50%": {
                    backgroundPosition: "-50% center",
                  },
                  "100%": {
                    backgroundPosition: "-50% center",
                  },
                },
              }}
            >
              have in mind.
            </Box>
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.5, sm: 1.5 },
              mb: 4,
              px: { xs: 2, sm: 2 },
              py: { xs: 1, sm: 0.75 },
              borderRadius: { xs: "12px", sm: "100px" },
              backgroundColor: "rgba(255, 107, 0, 0.04)",
              border: "1px solid rgba(255, 107, 0, 0.1)",
              textAlign: "center"
            }}
          >
            {/* Group 1: Pulsating Indicator + Text (Always together) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "8px",
                  height: "8px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#FF6B00",
                    borderRadius: "50%",
                    animation: "pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    "@keyframes pulseRing": {
                      "0%": { transform: "scale(0.8)", boxShadow: "0 0 0 0 rgba(255, 107, 0, 0.4)" },
                      "70%": { transform: "scale(1)", boxShadow: "0 0 0 6px rgba(255, 107, 0, 0)" },
                      "100%": { transform: "scale(0.8)", boxShadow: "0 0 0 0 rgba(255, 107, 0, 0)" }
                    }
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#FF6B00",
                    borderRadius: "50%",
                  }}
                />
              </Box>

              <Typography sx={{ fontFamily: "Poppins-Medium", fontSize: { xs: "10.5px", md: "14px" }, color: "#555555", letterSpacing: "0.2px" }}>
                Search across 200,000+ products from top brands
              </Typography>
            </Box>

            {/* Group 2: Explore Brands */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                onClick={() => navigate('/brands')}
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "10.5px", md: "14px" },
                  color: "#FF6B00",
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": { color: "#E66000" }
                }}
              >
                View All Brands
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ─── Gender Selector ─────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              background: "#F5F5F5",
              borderRadius: "100px",
              p: "4px",
              gap: "2px",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            {["Women", "Men", "Kids"].map((g) => {
              const isActive = gender === g;
              return (
                <Box
                  key={g}
                  onClick={() => setGender(g)}
                  sx={{
                    px: { xs: 2.5, sm: 3.5 },
                    py: "8px",
                    borderRadius: "100px",
                    cursor: "pointer",
                    fontFamily: "Poppins-Medium",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#fff" : "#666",
                    background: isActive
                      ? "#FF9B80"
                      : "transparent",
                    boxShadow: isActive
                      ? "0 4px 12px rgba(255,107,0,0.2)"
                      : "none",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      color: isActive ? "#fff" : "#FF6B00",
                      background: isActive
                        ? "#FF9B80"
                        : "rgba(255,107,0,0.06)",
                    },
                  }}
                >
                  {g}
                </Box>
              );
            })}
          </Box>
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "680px",
              display: "flex",
              alignItems: "center",
              background: "#FFFFFF",
              borderRadius: "20px",
              padding: { xs: "4px 6px 4px 16px", sm: "6px 8px 6px 24px" },
              boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
              border: "1.5px solid #EBEBEB",
              transition: "all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)",
              "&:hover": {
                borderColor: "rgba(255, 107, 0, 0.4)",
                boxShadow: "0 8px 30px rgba(255, 107, 0, 0.04), inset 0 2px 8px rgba(255, 107, 0, 0.02)",
              },
              "&:focus-within": {
                borderColor: "#FF6B00",
                boxShadow: "0 0 0 4px rgba(255, 107, 0, 0.12), 0 8px 35px rgba(255, 107, 0, 0.08), inset 0 2px 10px rgba(255, 107, 0, 0.04)",
              }
            }}
          >
            {/* Search Icon for Desktop Only */}
            <Search sx={{ display: { xs: "none", sm: "block" }, color: "#999", fontSize: "24px", mr: 1.5 }} />

            <Box sx={{ position: "relative", flex: 1, height: isMobile ? "50px" : "60px", display: "flex", alignItems: "center" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: { xs: "12px", sm: "16px", md: "18px" }, // Reduced by 1 on mobile
                  fontFamily: "Poppins-Regular",
                  color: "#111",
                }}
              />
              {!search && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#A0A0A0",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "12px", sm: "16px", md: "18px" }, // Reduced by 1 on mobile
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <span style={{ marginRight: "6px", flexShrink: 0 }}>Try</span>
                  <AnimatePresence mode="wait">
                    {showText && (
                      <motion.span
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          fontFamily: "Poppins-Medium",
                          color: "#777",
                          overflow: "hidden",
                          lineHeight: 1.3
                        }}
                      >
                        "{typedText}"
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, ml: { xs: 1, sm: 2 } }}>
              <Button
                onClick={() => navigate("/image-search")}
                variant="outlined"
                sx={{
                  minWidth: "auto",
                  padding: { xs: "6px", sm: "8px" },
                  borderRadius: "14px",
                  borderColor: "#EAEAEA",
                  color: "#555",
                  "&:hover": {
                    borderColor: "#FF6B00",
                    backgroundColor: "rgba(255,107,0,0.04)",
                    color: "#FF6B00"
                  }
                }}
                title="Search by Image"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </Button>
              <Button
                onClick={handleSearch}
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: "14px",
                  background: "#FF9B80",
                  color: "#FFF",
                  minWidth: "auto",
                  padding: { xs: "6px", sm: "8px 24px" }, // small icon padding on mobile, text padding on desktop
                  "&:hover": {
                    background: "#ff8a6a",
                  }
                }}
              >
                {/* Search Icon for Mobile */}
                <Search sx={{ display: { xs: "block", sm: "none" }, fontSize: "18px" }} />
                {/* Text for Desktop */}
                <Typography sx={{ display: { xs: "none", sm: "block" }, textTransform: "none", fontFamily: "Poppins-Medium", fontSize: "15px" }}>
                  Search
                </Typography>
              </Button>
            </Box>
          </Box>
        </motion.div>

        {/* Tagline before queries */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ width: "100%", textAlign: "center", marginTop: "12px", marginBottom: "0px" }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins-Medium",
              fontSize: { xs: "8px", sm: "10px" },
              color: "#535353ff",
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              opacity: 0.8
            }}
          >
            Describe it like a friend. We'll find it like a stylist.
          </Typography>
        </motion.div>

        {/* 3 Clickable Query Cards beneath search bar */}
        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}
        >
          <Grid container spacing={2} sx={{ maxWidth: "1100px", width: "100%", px: { xs: 2, sm: 0 } }}>
            {(() => {
              const currentQueries = queries && queries[gender] ? queries[gender].slice(0, 3) : [];

              // Find the longest query text to sync the font size uniformly across all 3 cards
              const maxQueryLength = currentQueries.reduce((max, q) => {
                const text = typeof q === 'string' ? q : (q.text || "");
                return Math.max(max, text.length);
              }, 0);

              // Dynamically adjust font size for longer queries to prevent overflow in fixed height cards
              let dynamicFontSize = { xs: "13px", sm: "14px", md: "14.5px" };
              let dynamicLineHeight = 1.35;

              if (maxQueryLength > 110) {
                dynamicFontSize = { xs: "8.5px", sm: "9px", md: "10px" };
                dynamicLineHeight = 1.2;
              } else if (maxQueryLength > 85) {
                dynamicFontSize = { xs: "9.5px", sm: "10px", md: "11.5px" };
                dynamicLineHeight = 1.25;
              } else if (maxQueryLength > 55) {
                dynamicFontSize = { xs: "11px", sm: "12px", md: "12.5px" };
                dynamicLineHeight = 1.3;
              } else if (maxQueryLength > 40) {
                dynamicFontSize = { xs: "11.5px", sm: "13px", md: "13.5px" };
              }

              return currentQueries.map((q, idx) => {
                const queryText = typeof q === 'string' ? q : q.text;
                const imageUrl = (typeof q !== 'string' && q.croppedImageUrl) ? q.croppedImageUrl : (typeof q === 'string' ? '' : q.imageUrl);

                // Signature peach, orange, and blue (pastel versions for readability)
                const cardColors = ["#FFECE5", "#FFF7E5", "#E5F0FF"];

                return (
                  <Grid item xs={12} sm={12} md={4} key={`${gender}-${idx}`}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 * idx }}
                      style={{ height: "100%" }}
                    >
                      <Box
                        onClick={() => navigate('/search', { state: { search: queryText, gender } })}
                        sx={{
                          display: "flex",
                          flexDirection: idx % 2 === 0 ? "row" : "row-reverse",
                          alignItems: "center",
                          backgroundColor: cardColors[idx % cardColors.length],
                          borderRadius: "10px",
                          border: "1px solid rgba(0,0,0,0.08)",
                          overflow: "hidden",
                          cursor: "pointer",
                          position: "relative",
                          height: { xs: "75px", sm: "80px", md: "95px" },
                          boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                          transition: "all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
                          "@keyframes cardPop": {
                            "0%, 100%": {
                              transform: "translateY(0px) scale(1)",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                              borderColor: "rgba(0,0,0,0.08)"
                            },
                            "15%": {
                              transform: "translateY(-5px) scale(1.02)",
                              boxShadow: "0 12px 24px rgba(0,0,0,0.11)",
                              borderColor: "rgba(255,107,0,0.3)"
                            },
                            "30%": {
                              transform: "translateY(0px) scale(1)",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                              borderColor: "rgba(0,0,0,0.08)"
                            }
                          },
                          animation: `cardPop 7s ease-in-out infinite`,
                          animationDelay: `${idx * 2}s`,
                          "&:hover": {
                            animation: "none",
                            transform: "translateY(-5px) scale(1.02)",
                            boxShadow: "0 12px 24px rgba(0,0,0,0.13)",
                            borderColor: "rgba(0,0,0,0.2)"
                          },
                          "&:active": { transform: "scale(0.98)" },
                          "@keyframes shine": {
                            "0%, 8%": { left: "-100%" },
                            "20%": { left: "220%" },
                            "100%": { left: "220%" }
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "50%",
                            height: "100%",
                            background: "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%)",
                            transform: "skewX(-25deg)",
                            animation: "shine 7s infinite ease-in-out",
                            animationDelay: `${idx * 2}s`,
                            pointerEvents: "none",
                            zIndex: 2
                          }
                        }}
                      >
                        {imageUrl && (
                          <Box sx={{
                            width: { xs: "68px", sm: "74px", md: "88px" },
                            minWidth: { xs: "68px", sm: "74px", md: "88px" },
                            height: "100%",
                            position: "relative",
                            overflow: "hidden",
                            flexShrink: 0,
                            borderRight: idx % 2 === 0 ? "1px solid rgba(0,0,0,0.08)" : "none",
                            borderLeft: idx % 2 !== 0 ? "1px solid rgba(0,0,0,0.08)" : "none"
                          }}>
                            <img
                              src={imageUrl}
                              alt="Query Preview"
                              style={{
                                position: "absolute",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                            />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, display: "flex", alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontFamily: "'Lora', serif",
                              fontSize: dynamicFontSize,
                              fontWeight: 500,
                              color: "#333",
                              lineHeight: dynamicLineHeight,
                              display: "-webkit-box",
                              WebkitLineClamp: 6, // Increased to 6 to allow more text
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            “{queryText}”
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              });
            })()}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePageSearchBar;
